import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { PendingFilters, SavedFilter } from '@/contexts/RightPanelContext';

interface SaveFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingFilters: PendingFilters;
  onSave: (name: string) => void;
  existingFilters: SavedFilter[];
  editingFilter?: SavedFilter | null;
}

export function SaveFilterModal({
  open,
  onOpenChange,
  pendingFilters,
  onSave,
  existingFilters,
  editingFilter,
}: Readonly<SaveFilterModalProps>) {
  const [filterName, setFilterName] = useState('');
  const [nameError, setNameError] = useState('');

  // Reset form when modal opens/closes or editing filter changes
  useEffect(() => {
    if (open) {
      if (editingFilter) {
        setFilterName(editingFilter.name);
      } else {
        setFilterName('');
      }
      setNameError('');
    }
  }, [open, editingFilter]);

  const handleSave = () => {
    const trimmedName = filterName.trim();

    // Validate name
    if (!trimmedName) {
      setNameError('Filter name is required');
      return;
    }

    if (trimmedName.length > 100) {
      setNameError('Filter name must be 100 characters or less');
      return;
    }

    // Check for duplicate names (excluding current filter if editing)
    const isDuplicate = existingFilters.some(
      (filter) => filter.name.toLowerCase() === trimmedName.toLowerCase() && filter.id !== editingFilter?.id
    );

    if (isDuplicate) {
      setNameError('A filter with this name already exists');
      return;
    }

    // Save the filter
    onSave(trimmedName);
    onOpenChange(false);
  };

  // Format filter summary for display - generic approach to handle all filter types
  const getFilterSummary = (): Array<{ label: string; value: string }> => {
    const summary: Array<{ label: string; value: string }> = [];

    // Return empty if pendingFilters is not available
    if (!pendingFilters) {
      return summary;
    }

    // Helper to safely check array fields
    const addArrayField = (field: any, label: string) => {
      if (Array.isArray(field) && field.length > 0) {
        summary.push({ label, value: field.join(', ') });
      }
    };

    // Helper to safely check date range fields
    const addDateRangeField = (field: any, label: string) => {
      if (field && (field.from || field.to)) {
        const from = field.from ? new Date(field.from).toLocaleDateString() : 'Any';
        const to = field.to ? new Date(field.to).toLocaleDateString() : 'Any';
        summary.push({ label, value: `${from} - ${to}` });
      }
    };

    // Helper to safely check min/max fields
    const addMinMaxField = (minField: any, maxField: any, label: string) => {
      if (minField || maxField) {
        const min = minField || '0';
        const max = maxField || '∞';
        summary.push({ label, value: `${min} - ${max}` });
      }
    };

    // Asset filters
    addArrayField(pendingFilters.selectedCategories, 'Category');
    addArrayField(pendingFilters.selectedStatuses, 'Status');
    addArrayField(pendingFilters.selectedLocations, 'Location');
    addDateRangeField(pendingFilters.acquisitionDateRange, 'Acquisition Date');
    addMinMaxField(pendingFilters.acquisitionCostMin, pendingFilters.acquisitionCostMax, 'Acquisition Cost');
    addMinMaxField(pendingFilters.netBookValueMin, pendingFilters.netBookValueMax, 'Net Book Value');
    addArrayField(pendingFilters.selectedAssignedTo, 'Assigned To');
    addArrayField(pendingFilters.selectedDepreciationMethods, 'Depreciation Method');
    addDateRangeField(pendingFilters.warrantyExpiryDateRange, 'Warranty Expiry Date');
    addArrayField(pendingFilters.selectedManufacturers, 'Manufacturer');

    // Work Item filters
    addArrayField(pendingFilters.selectedTaskStatuses, 'Work Item Status');
    addArrayField(pendingFilters.selectedTaskPriorities, 'Priority');
    addArrayField(pendingFilters.selectedTaskTypes, 'Work Item Type');
    addArrayField(pendingFilters.selectedTaskAssignees, 'Assignee');
    addDateRangeField(pendingFilters.taskDueDateRange, 'Due Date');
    addDateRangeField(pendingFilters.taskCreatedDateRange, 'Created Date');

    // Audit filters
    addArrayField(pendingFilters.selectedAuditStatuses, 'Audit Status');
    addArrayField(pendingFilters.selectedAuditTypes, 'Audit Type');
    addArrayField(pendingFilters.selectedAuditPriorities, 'Audit Priority');
    addArrayField(pendingFilters.selectedAuditors, 'Auditor');
    addDateRangeField(pendingFilters.auditScheduledDateRange, 'Scheduled Date');
    addDateRangeField(pendingFilters.auditCompletedDateRange, 'Completed Date');

    // Exception filters
    addArrayField(pendingFilters.selectedExceptionTypes, 'Exception Type');
    addArrayField(pendingFilters.selectedExceptionSeverities, 'Severity');
    addArrayField(pendingFilters.selectedExceptionStatuses, 'Exception Status');
    addDateRangeField(pendingFilters.exceptionDetectedDateRange, 'Detected Date');

    // Transaction/Enquiry filters
    addArrayField(pendingFilters.selectedTransactionTypes, 'Transaction Type');
    addDateRangeField(pendingFilters.transactionDateRange, 'Transaction Date');
    addMinMaxField(pendingFilters.transactionAmountMin, pendingFilters.transactionAmountMax, 'Amount');

    return summary;
  };

  const filterSummary = getFilterSummary();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingFilter ? 'Edit Filter' : 'Save Filter'}</DialogTitle>
          <DialogDescription>
            {editingFilter
              ? 'Update the name for this saved filter.'
              : 'Save your current filter configuration for quick access later.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Filter Name Input */}
          <div className="space-y-2">
            <Label htmlFor="filter-name">
              Filter Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="filter-name"
              value={filterName}
              onChange={(e) => {
                setFilterName(e.target.value);
                setNameError('');
              }}
              placeholder="e.g., Active IT Equipment"
              maxLength={100}
              className={nameError ? 'border-destructive' : ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSave();
                }
              }}
              autoFocus
            />
            {nameError && <p className="text-sm text-destructive">{nameError}</p>}
            <p className="text-xs text-muted-foreground">
              {filterName.length}/100 characters
            </p>
          </div>

          {/* Filter Summary */}
          <div className="space-y-2">
            <Label>Filters to be saved:</Label>
            <ScrollArea className="h-[200px] rounded-md border bg-muted/30 p-3">
              {filterSummary.length > 0 ? (
                <div className="space-y-2">
                  {filterSummary.map(({ label, value }) => (
                    <div key={`filter-${label}`} className="text-sm">
                      <span className="font-semibold">{label}:</span>{' '}
                      <span className="text-muted-foreground">{value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No filters configured</p>
              )}
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!filterName.trim() || !!nameError}>
            {editingFilter ? 'Update' : 'Save and Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
